import { useRef, useState } from "react";
import { Form, Formik, useField } from "formik";
import { FileText, Image as ImageIcon, Settings2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Loader } from "@/components/common/loader";
import { PageHeader } from "@/components/common/page-header";
import { useAuthedImage } from "@/hooks/use-authed-image";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ALIGN_OPTIONS,
  type FirmSettings,
  INVOICE_TERMS_OPTIONS,
  type InvoiceTerms,
  type LogoAlign,
  updateFirmSettings,
  uploadInvoiceLogo,
  uploadInvoiceQr,
} from "@/features/settings/firm-api";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { SelectField } from "@/features/settings/components/select-field";
import {
  FIELD_LABEL,
  SettingsSection,
  SettingsStickyBar,
} from "@/features/settings/components/settings-section";
import { useFirmSettings } from "@/features/settings/hooks/use-firm-settings";
import { ApiError } from "@/lib/api";

const schema = Yup.object({
  invoiceDefaultTaxPercent: Yup.number()
    .typeError("Enter a number")
    .min(0, "Must be 0 or more")
    .max(100, "Must be 100 or less")
    .required("Required"),
  invoiceDefaultTerms: Yup.string().required(),
  invoiceFooter: Yup.string().max(2000, "Footer is too long"),
  invoiceNextNumber: Yup.number()
    .typeError("Enter a number")
    .min(1, "Must be positive")
    .required("Required"),
  invoiceHeader: Yup.string().max(2000, "Header is too long"),
  invoiceHeaderAlign: Yup.string().required(),
  invoiceTopMargin: Yup.number()
    .typeError("Enter a number")
    .min(0, "Must be 0 or more")
    .max(200, "Must be 200 or less")
    .required("Required"),
  invoiceLogoAlign: Yup.string().required(),
  invoiceLogoScale: Yup.number()
    .typeError("Enter a number")
    .min(1, "1–100")
    .max(100, "1–100")
    .required("Required"),
  invoiceQrPrompt: Yup.string().max(120, "Too long"),
});

interface InvoiceValues {
  invoiceDefaultTaxPercent: string;
  invoiceDefaultTerms: InvoiceTerms;
  invoiceFooter: string;
  invoiceUseTax: boolean;
  invoiceNextNumber: string;
  invoiceHeader: string;
  invoiceHeaderAlign: LogoAlign;
  invoiceTopMargin: string;
  invoiceUseLogo: boolean;
  invoiceLogoAlign: LogoAlign;
  invoiceLogoScale: string;
  invoiceUseQr: boolean;
  invoiceQrPrompt: string;
}

/** Full-width Formik textarea (spans the section grid). */
function TextareaField({ name, label }: { name: string; label: string }) {
  const [field, meta] = useField(name);
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2 md:col-span-2 lg:col-span-3">
      <Label htmlFor={name} className={FIELD_LABEL}>
        {label}
      </Label>
      <Textarea id={name} rows={3} aria-invalid={hasError} {...field} />
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

/** Immediate multipart image upload (logo / QR) with a preview. */
function ImageUploadRow({
  label,
  imageUrl,
  mediaKind,
  isAdmin,
  onUpload,
  setSettings,
}: {
  label: string;
  imageUrl: string | null;
  /** Branding-asset kind for the authenticated media endpoint. */
  mediaKind: "invoice-logo" | "invoice-qr";
  isAdmin: boolean;
  onUpload: (file: File) => Promise<FirmSettings>;
  setSettings: (s: FirmSettings) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const src = useAuthedImage(
    imageUrl ? `/firm/settings/media/${mediaKind}?v=${encodeURIComponent(imageUrl)}` : null,
  );

  async function handleFile(file: File) {
    setSaving(true);
    try {
      setSettings(await onUpload(file));
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : `Couldn't upload the ${label.toLowerCase()}.`);
    } finally {
      setSaving(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="flex items-center gap-6">
      <div className="bg-muted flex size-24 items-center justify-center overflow-hidden rounded-lg">
        {src ? (
          <img src={src} alt={label} className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="text-muted-foreground text-xs">
            {imageUrl ? "Loading…" : "No image"}
          </span>
        )}
      </div>
      <div>
        <Label className="mb-1 block">{label}</Label>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!isAdmin || saving}
          onClick={() => fileRef.current?.click()}
        >
          {imageUrl ? "Replace" : "Upload"}
        </Button>
      </div>
    </div>
  );
}

export function InvoicesTab() {
  const { settings, setSettings, loading, error, isAdmin } = useFirmSettings();

  async function handleSubmit(values: InvoiceValues) {
    try {
      const updated = await updateFirmSettings({
        invoiceDefaultTaxPercent: values.invoiceDefaultTaxPercent,
        invoiceDefaultTerms: values.invoiceDefaultTerms,
        invoiceFooter: values.invoiceFooter,
        invoiceUseTax: values.invoiceUseTax,
        invoiceNextNumber: Number(values.invoiceNextNumber),
        invoiceHeader: values.invoiceHeader,
        invoiceHeaderAlign: values.invoiceHeaderAlign,
        invoiceTopMargin: Number(values.invoiceTopMargin),
        invoiceUseLogo: values.invoiceUseLogo,
        invoiceLogoAlign: values.invoiceLogoAlign,
        invoiceLogoScale: Number(values.invoiceLogoScale),
        invoiceUseQr: values.invoiceUseQr,
        invoiceQrPrompt: values.invoiceQrPrompt,
      });
      setSettings(updated);
      toast.success("Invoice settings saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save changes.");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Invoices"
        subtitle="Defaults and PDF-rendering settings for invoices."
        actions={
          <Button asChild variant="outline">
            <Link to="/settings/invoices/templates">Manage line-item templates →</Link>
          </Button>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error ? (
        <p className="text-destructive text-sm">
          Couldn&apos;t load this section. Please refresh and try again.
        </p>
      ) : (
        settings && (
          <>
            <Formik<InvoiceValues>
              initialValues={{
                invoiceDefaultTaxPercent: settings.invoiceDefaultTaxPercent,
                invoiceDefaultTerms: settings.invoiceDefaultTerms,
                invoiceFooter: settings.invoiceFooter,
                invoiceUseTax: settings.invoiceUseTax,
                invoiceNextNumber: String(settings.invoiceNextNumber),
                invoiceHeader: settings.invoiceHeader,
                invoiceHeaderAlign: settings.invoiceHeaderAlign,
                invoiceTopMargin: String(settings.invoiceTopMargin),
                invoiceUseLogo: settings.invoiceUseLogo,
                invoiceLogoAlign: settings.invoiceLogoAlign,
                invoiceLogoScale: String(settings.invoiceLogoScale),
                invoiceUseQr: settings.invoiceUseQr,
                invoiceQrPrompt: settings.invoiceQrPrompt,
              }}
              enableReinitialize
              validationSchema={schema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, dirty }) => (
                <Form className="flex flex-col gap-6">
                  <fieldset disabled={!isAdmin} className="flex flex-col gap-6">
                    <SettingsSection title="Defaults" icon={Settings2}>
                      <FormField
                        name="invoiceDefaultTaxPercent"
                        label="Default Tax Rate (%)"
                        type="number"
                        step="0.01"
                        min={0}
                        max={100}
                        labelClassName={FIELD_LABEL}
                      />
                      <SelectField
                        name="invoiceDefaultTerms"
                        label="Default Payment Terms"
                        options={INVOICE_TERMS_OPTIONS}
                        labelClassName={FIELD_LABEL}
                      />
                      <FormField
                        name="invoiceNextNumber"
                        label="Next Invoice Number"
                        type="number"
                        min={1}
                        labelClassName={FIELD_LABEL}
                      />
                      <CheckboxField
                        name="invoiceUseTax"
                        label="Show tax on invoices"
                        hint="When off, the PDF hides Subtotal/Tax rows."
                      />
                    </SettingsSection>

                    <SettingsSection title="PDF Layout" icon={FileText}>
                      <TextareaField name="invoiceHeader" label="Header" />
                      <SelectField
                        name="invoiceHeaderAlign"
                        label="Header Alignment"
                        options={ALIGN_OPTIONS}
                        labelClassName={FIELD_LABEL}
                      />
                      <FormField
                        name="invoiceTopMargin"
                        label="Top Margin (pt)"
                        type="number"
                        min={0}
                        max={200}
                        labelClassName={FIELD_LABEL}
                      />
                      <TextareaField name="invoiceFooter" label="Footer / Message" />
                    </SettingsSection>

                    <SettingsSection title="Logo & QR" icon={ImageIcon}>
                      <CheckboxField name="invoiceUseLogo" label="Show logo on invoices" />
                      <SelectField
                        name="invoiceLogoAlign"
                        label="Logo Alignment"
                        options={ALIGN_OPTIONS}
                        labelClassName={FIELD_LABEL}
                      />
                      <FormField
                        name="invoiceLogoScale"
                        label="Logo Scale (%)"
                        type="number"
                        min={1}
                        max={100}
                        labelClassName={FIELD_LABEL}
                      />
                      <CheckboxField name="invoiceUseQr" label="Show payment QR on invoices" />
                      <FormField
                        name="invoiceQrPrompt"
                        label="QR Caption"
                        labelClassName={FIELD_LABEL}
                      />
                    </SettingsSection>
                  </fieldset>

                  {!isAdmin ? (
                    <p className="text-muted-foreground text-sm">
                      Only a country director can edit invoice settings.
                    </p>
                  ) : (
                    <SettingsStickyBar>
                      <Button type="submit" disabled={isSubmitting || !dirty}>
                        {isSubmitting ? "Saving..." : "Save Changes"}
                      </Button>
                    </SettingsStickyBar>
                  )}
                </Form>
              )}
            </Formik>

            <SettingsSection title="Images" icon={Upload} bodyClassName="block">
              <div className="flex flex-col gap-6">
                <ImageUploadRow
                  label="Invoice Logo"
                  imageUrl={settings.invoiceLogoUrl}
                  mediaKind="invoice-logo"
                  isAdmin={isAdmin}
                  onUpload={uploadInvoiceLogo}
                  setSettings={setSettings}
                />
                <ImageUploadRow
                  label="Payment QR"
                  imageUrl={settings.invoiceQrImageUrl}
                  mediaKind="invoice-qr"
                  isAdmin={isAdmin}
                  onUpload={uploadInvoiceQr}
                  setSettings={setSettings}
                />
              </div>
            </SettingsSection>
          </>
        )
      )}
    </div>
  );
}
